import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { AiCountPage } from "./ai-count-types";

export function useAiReviewNavigation({
  page,
  activePage,
  viewportRef,
  focusPagePoint,
}: {
  page: AiCountPage | undefined;
  activePage: number;
  viewportRef: RefObject<DocumentViewportValue | null>;
  focusPagePoint: (point: { x: number; y: number }) => void;
}) {
  const [focusRequest, setFocusRequest] = useState<{
    id: string;
    sequence: number;
  }>();
  const navigationIndex = useRef<Record<string, number>>({});
  const focusedRequest = useRef<typeof focusRequest>(undefined);

  const navigate = useCallback(
    (tagId: string, direction: number) => {
      if (!page?.navigationTagIds.some((id) => id === tagId)) return false;
      const marks = page.navigationProposals
        .filter((mark) => mark.tagId === tagId)
        .sort((a, b) => a.pageNumber - b.pageNumber || a.y - b.y || a.x - b.x);
      if (!marks.length) return false;
      const index =
        ((navigationIndex.current[tagId] ?? (direction === 1 ? -1 : 0)) +
          direction +
          marks.length) %
        marks.length;
      navigationIndex.current[tagId] = index;
      const mark = marks[index]!;
      viewportRef.current?.goToPage(mark.pageNumber);
      setFocusRequest((old) => ({
        id: mark._id,
        sequence: (old?.sequence ?? 0) + 1,
      }));
      if (mark.pageNumber === activePage) focusPagePoint(mark);
      return true;
    },
    [page, activePage, focusPagePoint, viewportRef],
  );

  useEffect(() => {
    if (!focusRequest || focusedRequest.current === focusRequest) return;
    const mark = page?.proposals.find(
      (proposal) =>
        proposal._id === focusRequest?.id && proposal.pageNumber === activePage,
    ) ?? page?.navigationProposals.find(
      (proposal) =>
        proposal._id === focusRequest.id && proposal.pageNumber === activePage,
    );
    if (mark) {
      focusedRequest.current = focusRequest;
      focusPagePoint(mark);
    }
  }, [activePage, focusRequest, page, focusPagePoint]);

  return { focusRequest, setFocusRequest, navigate };
}
